import {
  Box,
  Skeleton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "../../context/GlobalContext";
import {
  createCatalog,
  pdfDownloadLink,
  syncOrder,
  useOrderTableDetails,
} from "../../features/order/api/OrderTableDetail";
import OrderPdfComp from "../../features/order/OrderPdfComp";
import { getIcon } from "../../utils/iconConfig";
import CustomTooltip from "./CustomTooltip";
import ActionModal from "./modals/ordersModal/ActionModal";
import PdfLoader from "./PdfLoader";
import InfoModal from "./infoModal/InfoModal";
import LogoutandDeleteModal from "./modals/LogoutandDeleteModal/LogoutandDeleteModal";
import CatalogModal from "./modals/catalogModal/CatalogModal";
import CustomToast from "../ui/CustomToast";
import { toast } from "react-hot-toast";
import { getClientLabel } from "../../utils/clientLabel";

// Memoized components to prevent unnecessary re-renders
const MemoizedTableRow = memo(({ children }) => (
  <TableRow>{children}</TableRow>
));
const MemoizedTableCell = memo(({ children, ...props }) => (
  <TableCell {...props}>{children}</TableCell>
));

const StyledTableBodyCell = styled(MemoizedTableCell)(({ theme }) => ({
  fontFamily: "Karla !important",
  padding: ".5vw .25vw",
  border: "none",
  fontSize: ".7vw",
  color: `${theme?.palette?.common?.white} !important`,
}));

const StyledTableRow = styled(MemoizedTableRow)(() => ({}));

const TableSkeleton = memo(({ columns, rows = 5 }) => (
  <>
    {Array.from({ length: rows })?.map((_, rowIndex) => (
      <StyledTableRow key={`skeleton-${rowIndex}`}>
        {Array.from({ length: columns })?.map((_, cellIndex) => (
          <StyledTableBodyCell key={`skeleton-cell-${cellIndex}`}>
            <Skeleton
              variant="text"
              sx={{ fontSize: "1rem" }}
              animation="wave"
              width="100%"
            />
          </StyledTableBodyCell>
        ))}
      </StyledTableRow>
    ))}
  </>
));

const OrdersTable = () => {
  const theme = useTheme();
  const isExtraLargeScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const navigate = useNavigate();
  const { search, filterDate, filterEvent, setCartData, setHomeData, setDetails } =
    useGlobalContext();

  const printRef = useRef();
  const [selectedData, setSelectedData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [currentElementData, setCurrentElementData] = useState(null);
  const [selectAction, setSelectAction] = useState("");
  const [loader, setLoader] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [restrictModal, setRestrictModal] = useState({
    isOpen: false,
    data: null,
  });
  const [syncModal, setSyncModal] = useState({
    isOpen: false,
    data: null,
  });
  const [catalogModal, setCatalogModal] = useState({
    isOpen: false,
    data: null,
  });

  const formatToYMD = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date?.getTime())) return "";
    const year = date?.getFullYear();
    const month = String(date.getMonth() + 1)?.padStart(2, "0");
    const day = String(date.getDate())?.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useOrderTableDetails({
    limit: 12,
    searchBy: search,
    filterBy: "date",
    startDate: formatToYMD(filterDate?.startDate),
    endDate: formatToYMD(filterDate?.endDate),
    eventTypeId: filterEvent,
  });

  const rights = JSON.parse(localStorage.getItem("rights")) || {};
  
  const hasShareAccess = rights["Share Access"]?.share
  const hasDownloadAccess = rights["Share Access"]?.download

  useEffect(() => {
    if (data?.pages?.[0]?.status === 401) {
      setAccessDenied(true);
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [search, filterDate?.startDate, filterDate?.endDate, filterEvent, refetch]);

  // Stable data reference
  const clientDataTable = useMemo(() => {
    return data?.pages?.flatMap((page) => page?.data?.orderData || []) || [];
  }, [data?.pages]);
  useEffect(() => {
    if (isLoading || isFetchingNextPage || !hasNextPage) return;
    const el = document.getElementById("scrollable-table-container");
    if (el && el.scrollHeight <= el.clientHeight) fetchNextPage();
  }, [clientDataTable.length, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

  useMemo(() => {
    const summary = data?.pages[0]?.data?.orderSummary || {};
    const safeToFixed = (value, digits = 3) =>
      typeof value === "number" ? value.toFixed(digits) : 0;
    setDetails({
      diamondWeight: safeToFixed(summary?.allTotalDiamondWt),
      goldWeight: safeToFixed(summary?.allTotalGoldWt),
      designCount: summary?.allTotalDesignsQty || 0,
    });
    return () => {
      setDetails({
        prevPurchase: 0,
        diamondWeight: 0,
        goldWeight: 0,
        designCount: 0,
        categories: 0,
        cartScore: 0,
      });
    };
  }, [data?.pages]);

  const handleClose = useCallback(() => {
    setOpenModal(false);
    setCurrentElementData(null);
  }, []);

  const syncOrderGati = useCallback(async (orderData) => {
    try {
      setLoader(true);
      console.log(orderData);
      const payload = {
        orderNumber: orderData?.orderNumber,
        projectName : "SELECT"
      }
      
      const response = await syncOrder(payload); 
      if (response?.status === 200 || response?.status === 201) {
         setSyncModal({ isOpen: false, data: null });
         refetch();
          toast.custom((t) => (
    <CustomToast
      t={t}
      message={response?.message || "Order synced successfully"}
    />
  ));
      } else {
         toast.custom((t) => (
          <CustomToast
            t={t}
            message={response?.message || "Order synced Failed"}
          />
        ));
      }
    } catch (error) {
      console.error("Error syncing custom order:", error);

       toast.custom((t) => (
          <CustomToast
            t={t}
            message={error?.response?.data?.message || "Error syncing order"}
          />
        ));
    } finally {
      setLoader(false);
    }
  }, [refetch]);

  const handleOpen = useCallback((data, actions) => {
    setOpenModal(true);
    if (actions === "emails") setCurrentElementData(data);
    if (actions === "whatsapp") setCurrentElementData(data);
    setSelectAction(actions);
  }, []);

  const handleEditCart = useCallback(
    (item) => {
      setDetails({
        diamondWeight: item?.allTotalDiamondWt || 0,
        goldWeight: item?.allTotalGoldWt || 0,
        designCount: item?.allTotalDesignsQty || 0,
      });
      localStorage.setItem("scanned", true);

      const clientData = {
        name: item?.clientName,
        emails: item?.matchClientMobileNumber?.emails,
        phoneNumbers: item?.matchClientMobileNumber?.whatsAppNos,
      };

      localStorage.setItem("ClientData", JSON.stringify(clientData));
      navigate("/home");
    },
    [navigate, setCartData, setHomeData]
  );

  const generatePdf = useCallback(async (data) => {
    try {
      setLoader(true);
      setSelectedData(data);
      if (!data.gatiOrderId) {
        setLoader(false);

        alert("PDF is Not available in Gati !");
        return false;
      }
      let res = await pdfDownloadLink(data?.gatiOrderId);

      if (res?.data?.status !== 200) {
        setLoader(false);
        alert(res?.response?.data?.message);
        return;
      } else {
        window.open(res?.data?.data, "_blank");
        setLoader(false);
        return;
      }
    } catch (error) {
      setLoader(false);

      alert(error?.message);
    }
  }, []);

  const getLoggedInUserId = useCallback(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.aud || null;
    } catch (error) {
      return null;
    }
  }, []);

  const handleCreateCatalog = useCallback(
    async (item) => {
      try {
        const userRef = getLoggedInUserId();

        if (!userRef) {
          toast.custom((t) => (
            <CustomToast
              t={t}
              isError
              message={"User not found. Please re-login."}
            />
          ));
          return;
        }

        if (!item?.styleIds?.length) {
          toast.custom((t) => (
            <CustomToast
              t={t}
              isError
              message={"No designs found in this order."}
            />
          ));
          return;
        }

        setLoader(true);

        const payload = {
          catalogName: item?.gatiOrderNo,
          selectedDesignIds: item?.styleIds,
          userRef,
        };

        const response = await createCatalog(payload);

        const isSuccess =
          response?.status === 200 || response?.status === 201;

        toast.custom((t) => (
          <CustomToast
            t={t}
            isError={!isSuccess}
            message={
              response?.message ||
              (isSuccess
                ? "Catalog created successfully"
                : "Error creating catalog")
            }
          />
        ));
      } catch (error) {
        console.error("Error creating catalog:", error);
        toast.custom((t) => (
          <CustomToast
            t={t}
            isError
            message={
              error?.response?.data?.message || "Error creating catalog"
            }
          />
        ));
      } finally {
        setLoader(false);
      }
    },
    [getLoggedInUserId]
  );

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date?.getDate())?.padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date?.getFullYear();
    let hours = date?.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day} ${month} ${year}, ${String(hours)?.padStart(
      2,
      "0"
    )}:${minutes} ${ampm}`;
  }, []);

  const renderTooltipContent = useCallback(
    (items, action) => (
      <ol style={{ margin: 0, paddingLeft: ".4vw" }}>
        {action === "emails"
          ? items.map((item, index) => <li key={index}>{item}</li>)
          : items.map((item, index) => (
            <li key={index}>{`${item?.slice(0, 2)}${item?.slice(2, 25)}`}</li>
          ))}
      </ol>
    ),
    []
  );

  function formatMobileNumber(number) {
    const str = number.toString();
    if (str.startsWith("91") && str.length > 10) {
      return `+91 ${str.slice(2)}`;
    } else if (str.startsWith("+91 ") && str.length > 10) {
      return `+91 ${str.slice(3)}`;
    } else if (str.length <= 10) {
      return `+91 ${str}`;
    }

    return str;
  }
  const StyledTableContainer = useMemo(
    () =>
      styled(TableContainer)(() => ({
        backgroundColor: "transparent",
        // Shrink to the space OrderScreen leaves; grows only up to content.
        flex: "0 1 auto",
        minHeight: 0,
        overflowX: "auto",
        position: "relative",
        border: ".05vw solid #fff",
        scrollbarWidth: "none", // Firefox
        "&::-webkit-scrollbar": {
          height: ".3vw",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: ".15vw",
        },
        scrollbarWidth: "none",
        "-ms-overflow-style": "none",
      })),
    [isExtraLargeScreen]
  );

  const StyledTable = useMemo(
    () =>
      styled(Table)(() => ({
        "& thead": {
          backgroundColor: "#444444",
          position: "sticky",
          top: 0,
          zIndex: 1,
          "& th": {
            fontSize: ".7vw",
            fontWeight: 400,
            lineHeight: "1.1vw",
            color: "#FFFFFF",
            textAlign: "center",
            fontFamily: "Poppins",
            padding: ".5vw",
            borderBottom: ".05vw solid #fff",
          },
        },
        "& tbody": {
          "& td": {
            fontSize: ".7vw",
            fontWeight: 400,
            lineHeight: "1vw",
            color: "#FFFFFF",
            textAlign: "center",
            fontFamily: "Poppins",
            borderBottom: ".05vw solid #fff",
            padding: ".5vw .25vw",
            border: ".05vw solid #fff",
          },
        },
        "& th": {
          backgroundColor: "#444444",
          height: "2.5vw",
          position: "sticky",
          top: 0,
          zIndex: 1,
        },
      })),
    []
  );

  const StyleIcons = useMemo(
    () =>
      styled(Typography)(({ color, apply }) => ({
        border: `.05vw solid ${color}`,
        borderRadius: ".25vw",
        width: !apply && "2vw",
        minHeight: "1.4vw",
        padding: !apply && ".25vw",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
      })),
    []
  );

  const RowComponent = memo(({ item, index }) => {
    return (
      <StyledTableRow>
        <StyledTableBodyCell>{index + 1}</StyledTableBodyCell>
        <StyledTableBodyCell sx={{ textTransform: "capitalize" }}>
          {getClientLabel(item)}
        </StyledTableBodyCell>
        <StyledTableBodyCell>{formatDate(item?.createdAt)}</StyledTableBodyCell>
        <StyledTableBodyCell>
          {item?.matchClientMobileNumber?.whatsAppNos?.length > 0 ? (
            <CustomTooltip
              title={renderTooltipContent(
                item?.matchClientMobileNumber?.whatsAppNos
              )}
              large
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={".3vw"}
              >
                {formatMobileNumber(item?.matchClientMobileNumber?.whatsAppNos[0])}
                {getIcon("eye")}
              </Box>
            </CustomTooltip>
          ) : (
            "N/A"
          )}
        </StyledTableBodyCell>
        <StyledTableBodyCell>
          {item?.matchClientMobileNumber?.emails.length > 0 ? (
            <CustomTooltip
              title={renderTooltipContent(item.matchClientMobileNumber?.emails)}
              large
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={".3vw"}
              >
                {item?.matchClientMobileNumber?.emails[0]}
                {getIcon("eye")}
              </Box>
            </CustomTooltip>
          ) : (
            "N/A"
          )}
        </StyledTableBodyCell>
        <StyledTableBodyCell>{item?.eventName || "—"}</StyledTableBodyCell>
        <StyledTableBodyCell>{item?.totalDesignsQty}</StyledTableBodyCell>
        <StyledTableBodyCell>
          {item?.totalDiamondWt.toFixed(3)} ct
        </StyledTableBodyCell>
        <StyledTableBodyCell>
          {item?.totalGoldWt.toFixed(3)} g
        </StyledTableBodyCell>
        <StyledTableBodyCell>{item?.cart_score || 0}%</StyledTableBodyCell>
        <StyledTableBodyCell>
          {item.salesPerson?.fullName || ""}
        </StyledTableBodyCell>
        <StyledTableBodyCell>
          <Box display="flex" gap=".4vw" justifyContent="center">
            <StyleIcons
              color="white"
              onClick={() => handleOpen(item, "emails")}
            >
              {getIcon("email")}
            </StyleIcons>
            {hasShareAccess && <StyleIcons
              color="#60D669"
              onClick={() => handleOpen(item, "whatsapp")}
            >
              {getIcon("WhatsApp")}
            </StyleIcons>}
            {hasDownloadAccess && <StyleIcons color="#E77B7B" onClick={() => generatePdf(item)}>
              {getIcon("pdf")}
            </StyleIcons>}
            <StyleIcons
              color="white"
              onClick={() => setCatalogModal({ isOpen: true, data: item })}
            >
              {getIcon("Catalog")}
            </StyleIcons>
            <StyleIcons apply onClick={() => handleEditCart(item)}>
              {/* {getIcon("edit")} */}
            </StyleIcons>
            <StyleIcons
              apply
              onClick={() => setRestrictModal({ isOpen: true, data: item })}
            >
              {getIcon("RestrictIcon")}
            </StyleIcons>

            {!item?.gatiOrderId && (
              <StyleIcons
                apply={true}                          
                onClick={() => setSyncModal({ isOpen: true, data: item })}
               
              >
                {getIcon("Sync")}                     
              </StyleIcons>
            )}
            
          </Box>
        </StyledTableBodyCell>
      </StyledTableRow>
    );
  });

  return (
    <>
      {loader && <PdfLoader open={loader} />}
      {!accessDenied ? (
        <StyledTableContainer id="scrollable-table-container">
          <InfiniteScroll
            dataLength={clientDataTable.length}
            next={fetchNextPage}
            hasMore={hasNextPage}
            scrollableTarget="scrollable-table-container"
            scrollThreshold="5vw"
            style={{ overflow: "visible" }}
          >
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableCell>Sr no.</TableCell>
                  <TableCell>Client Name</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Mobile Number</TableCell>
                  <TableCell>Email Address</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Designs Qty</TableCell>
                  <TableCell>Diamond Wt</TableCell>
                  <TableCell>Gold Wt</TableCell>
                  <TableCell>Cart Score</TableCell>
                  <TableCell>Sales Person</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableSkeleton columns={12} rows={12} />
                ) : (
                  <>
                    {clientDataTable?.map((item, index) => (
                      <RowComponent
                        key={`${item?._id}-${index}`}
                        item={item}
                        index={index}
                      />
                    ))}
                    {isFetchingNextPage && (
                      <TableSkeleton columns={12} rows={3} />
                    )}
                  </>
                )}
              </TableBody>
            </StyledTable>
          </InfiniteScroll>
        </StyledTableContainer>
      ) : (
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "3vw",
            color: "red",
            fontFamily: "Poppins",
          }}
        >
          {data?.pages?.[0]?.message}
        </div>
      )}

      <OrderPdfComp printRef={printRef} selectedData={selectedData} />
      <ActionModal
        openModal={openModal}
        handleClose={handleClose}
        data={currentElementData}
        selectAction={selectAction}
        refetch={refetch}
        setLoader={setLoader}
      />
      <InfoModal
        type={"restrict"}
        headLabel={`Restricted Designs of ${restrictModal?.data?.clientName}`}
        Open={restrictModal.isOpen}
        handleClose={() => setRestrictModal({ isOpen: false, data: null })}
        restrictModal={restrictModal}
      />
      <LogoutandDeleteModal
        openModal={syncModal.isOpen}
        handleClose={() => setSyncModal({ isOpen: false, data: null })}
        deleteItem={syncModal?.data}
        data={syncModal?.data}
        onAction={syncOrderGati}
        modalType={"sync"}
      />
      <CatalogModal
        openModal={catalogModal.isOpen}
        handleClose={() => setCatalogModal({ isOpen: false, data: null })}
        data={catalogModal?.data}
        onAction={handleCreateCatalog}
      />
    </>
  );
};

export default OrdersTable;

